'use client';

import './dialog.scss';

import { forwardRef } from 'react';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';

import {
  ButtonStyle,
  ButtonStyleProps,
} from '../../atoms/button-style/button-style';
import { cn } from '../../utils/helpers';
import {
  Modal,
  ModalClose,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalProps,
  ModalTitle,
  ModalTrigger,
  ModalTriggerProps,
} from '../modal/modal';

export type DialogProps = ModalProps;

export const Dialog = Modal;

/**
 * Dialog content.
 */
export type DialogContentProps = ModalContentProps & {
  centered?: boolean;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  supportingText?: string;
  purpose?: 'default' | 'neutral' | 'positive' | 'warning' | 'negative';
  customIcon?: React.ReactNode;
  icon?: IconNames;
  footer?: React.ReactNode;
};
export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      title,
      supportingText,
      size = 'md',
      style,
      purpose = 'neutral',
      customIcon,
      icon,
      footer,
      ...props
    },
    ref,
  ) => {
    return (
      <ModalContent
        ref={ref}
        aria-describedby={undefined}
        className={cn(
          `Dialog__content flex flex-col p-comfy-sm md:p-comfy-lg Dialog__content--${size}`,
        )}
        style={{
          ...style,
        }}
        {...props}
      >
        {/* Header */}
        <header className="flex flex-col pb-comfy-sm">
          {/* Icon and close button */}
          <div
            className={cn(' flex items-center  justify-end pb-comfy-sm', {
              'justify-between': icon || customIcon,
            })}
          >
            {(icon || customIcon) && (
              <div
                className={cn(
                  `Dialog__content__icon
                Dialog__content__icon--${purpose} flex items-center justify-center rounded-full`,
                )}
              >
                {customIcon || (icon && <ArkIcon size="24" iconName={icon} />)}
              </div>
            )}

            <ModalCloseButton
              className="Dialog__close mr-comfy-xs mt-comfy-xs"
              name="Dialog"
            />
          </div>

          {/* Heading */}
          <div className="pt-comfy-sm">
            <ModalTitle className="Takeover__title heading-sm ">
              {title}
              <span className="block pt-snug-md body-sm body-neutral-mild">
                {supportingText}
              </span>
            </ModalTitle>
          </div>
        </header>

        {/* Body */}
        {props.children && (
          <div className="overflow-auto py-comfy-sm">{props.children}</div>
        )}

        {/* Footer */}
        {footer && <div className="pt-comfy-sm">{footer}</div>}
      </ModalContent>
    );
  },
);

/**
 * Dialog trigger.
 */
export type DialogTriggerProps = ModalTriggerProps;
export const DialogTrigger = ModalTrigger;

/**
 * Dialog close.
 */
export const DialogClose = ModalClose;

export interface DialogActionsProps {
  primaryBtn?: ButtonStyleProps;
  secondaryBtn?: ButtonStyleProps;
}

export const DialogActions: React.FC<DialogActionsProps> = ({
  primaryBtn,
  secondaryBtn,
}) => {
  return (
    <div className="flex flex-row gap-comfy-xs md:justify-end">
      {secondaryBtn && (
        <DialogClose asChild>
          <ButtonStyle
            className="w-full md:w-fit"
            size="md"
            purpose="neutral"
            variant="secondary"
            {...secondaryBtn}
          />
        </DialogClose>
      )}
      {primaryBtn && (
        <ButtonStyle className="w-full md:w-fit" size="md" {...primaryBtn} />
      )}
    </div>
  );
};
